import { Alert as MuiAlert } from '@material-ui/lab'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons'
import { Box, Button, useTheme, Link, ClickAwayListener, Popper, Paper, Typography } from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMegaphone } from '@fortawesome/pro-regular-svg-icons'
import useUpdateHighSchoolExplorerPreference from './useUpdateHighSchoolExplorerPreference'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import userActions from 'store/user/actions'
import { useHistory } from 'components/ReactRouterProvider/context'
import { useAppSelector } from 'store'

function useTransitionPreference({ redirectIfPreferenceIs }: { redirectIfPreferenceIs: boolean }) {
  const { mutate, isSuccess, isLoading } = useUpdateHighSchoolExplorerPreference()

  const dispatch = useDispatch()
  const history = useHistory()

  const user = useAppSelector((state) => state.user.record)

  useEffect(() => {
    if (isSuccess) {
      dispatch(userActions.fetchUser())
    }
  }, [isSuccess, dispatch])

  useEffect(() => {
    if (user?.Preferences?.UseHighSchoolExplorer === redirectIfPreferenceIs) {
      history.replace(`${import.meta.env.BASE_URL}/recruit`)
    }
  }, [user?.Preferences?.UseHighSchoolExplorer, history, redirectIfPreferenceIs])

  return { mutate, isLoading }
}

export const Alert = withStyles((theme) => ({
  root: {
    color: theme.palette.secondary.main,
    background: theme.palette.secondary.light,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
}))(MuiAlert)

export function HighSchoolExplorerOptIn() {
  const { mutate, isLoading } = useTransitionPreference({ redirectIfPreferenceIs: true })
  const theme = useTheme()

  return (
    <Box bgcolor={theme.palette.secondary.light}>
      <Box
        maxWidth={650}
        margin="auto"
      >
        <Alert
          icon={
            <FontAwesomeIcon
              icon={faMegaphone}
              color={theme.palette.secondary.main}
              size="sm"
            />
          }
        >
          <Box
            display="flex"
            alignItems="center"
            gridGap={theme.spacing(2)}
          >
            We're working on a new High School Explorer. Want an early look?
            <Button
              size="small"
              color="inherit"
              onClick={() => mutate({ enabled: true })}
              disabled={isLoading}
            >
              Try it Out
            </Button>
          </Box>
        </Alert>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles((theme) => {
  return {
    root: {
      margin: 0,
      top: 'auto',
      left: 20,
      bottom: 20,
      right: 'auto',
      position: 'fixed',
      zIndex: theme.zIndex.modal - 1,
      backgroundColor: '#7457FF',
      '&:hover': {
        backgroundColor: '#7457FF',
      },
    },
    paper: {
      marginBottom: theme.spacing(1),
      padding: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        width: '350px',
      },
    },
    popper: {
      zIndex: theme.zIndex.modal - 1,
    },
    linkText: {
      color: theme.palette.text.secondary,
    },
    myIconSizeSmall: {
      '& > *:first-child': {
        fontSize: '18px',
      },
    },
  }
})

export function HighSchoolExplorerOptOut() {
  const { mutate, isLoading } = useTransitionPreference({ redirectIfPreferenceIs: false })
  const [open, setOpen] = useState(false)
  const fabRef = useRef<HTMLButtonElement>(null)
  const handleClick = () => {
    setOpen(!open)
  }

  const classes = useStyles()

  return (
    <>
      <Button
        color="primary"
        onClick={handleClick}
        className={classes.root}
        classes={{ startIcon: classes.myIconSizeSmall }}
        ref={fabRef}
        variant="contained"
        startIcon={<FontAwesomeIcon icon={faThumbsUp} />}
      >
        Share Feedback
      </Button>
      <Popper
        open={open}
        anchorEl={fabRef.current}
        className={classes.popper}
        style={{
          marginLeft: '16px',
        }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={6}
            className={classes.paper}
          >
            <>
              <p>
                <Typography variant="subtitle2">Welcome to the new High School Explorer!</Typography>
              </p>
              <p>
                <Typography variant="body2">
                  Share what you like, what's missing, and if something isn't working by{' '}
                  <Link
                    className={classes.linkText}
                    underline="always"
                    target="_blank"
                    href="https://scoir-network.typeform.com/to/ooDu9Bff"
                  >
                    leaving feedback.
                  </Link>
                </Typography>
              </p>
              <Box
                display="flex"
                justifyContent="flex-end"
                mt={4}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => mutate({ enabled: false })}
                  disabled={isLoading}
                >
                  Go back to the old version
                </Button>
              </Box>
            </>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
